import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from './apiClient';
import { getCSRFToken, generateCSRFToken } from '../utils/csrf';
import { sanitizeErrorMessage } from '../utils/errorHandler';

/**
 * Custom hook for fetching data from an API endpoint
 * @param {string} endpoint - API endpoint
 * @param {Array} dependencies - Dependencies that trigger refetch
 * @param {Object} options - Configuration options
 * @returns {Object} { data, loading, error, refetch }
 */
export const useApiData = (endpoint, dependencies = [], options = {}) => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const isMountedRef = useRef(true);

	const {
		immediate = true,
		onSuccess,
		onError,
		transform = (data) => data
	} = options;

	// Use refs to avoid recreating fetchData on every render
	const onSuccessRef = useRef(onSuccess);
	const onErrorRef = useRef(onError);
	const transformRef = useRef(transform);

	useEffect(() => {
		onSuccessRef.current = onSuccess;
		onErrorRef.current = onError;
		transformRef.current = transform;
	});

	const fetchData = useCallback(async () => {
		if (!endpoint) return;

		try {
			setLoading(true);
			setError(null);

			const response = await api.get(endpoint);

			if (isMountedRef.current) {
				const transformedData = transformRef.current(response.data);
				setData(transformedData);
				onSuccessRef.current?.(transformedData);
			}
		} catch (err) {
			console.error('useApiData API error:', err);
			if (isMountedRef.current) {
				const errorMessage = sanitizeErrorMessage(err.response?.data?.message || err.message || 'An error occurred');
				setError(errorMessage);
				onErrorRef.current?.(err);
			}
		} finally {
			if (isMountedRef.current) {
				setLoading(false);
			}
		}
	}, [endpoint]);

	useEffect(() => {
		isMountedRef.current = true; // Ensure mounted ref is true when effect runs

		if (immediate) {
			fetchData();
		}

		return () => {
			isMountedRef.current = false;
		};
	}, [immediate, fetchData, ...dependencies]);

	return { data, loading, error, refetch: fetchData };
};

/**
 * Custom hook for data mutations (POST, PUT, DELETE)
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method (default: 'POST')
 * @returns {Object} { mutate, loading, error, data }
 */
export const useApiMutation = (endpoint, method = 'POST') => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);

	// Ensure CSRF token is available
	useEffect(() => {
		if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase())) {
			if (!getCSRFToken()) {
				generateCSRFToken();
			}
		}
	}, [method]);

	const mutate = useCallback(async (payload, customEndpoint) => {
		const url = customEndpoint || endpoint;

		try {
			setLoading(true);
			setError(null);

			// Add CSRF token to headers for state-changing operations
			const config = {};
			if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase())) {
				const csrfToken = getCSRFToken();
				if (csrfToken) {
					config.headers = {
						'X-CSRF-Token': csrfToken
					};
				}
			}

			let response;
			switch (method.toUpperCase()) {
			case 'POST':
				response = await api.post(url, payload, config);
				break;
			case 'PUT':
				response = await api.put(url, payload, config);
				break;
			case 'PATCH':
				response = await api.patch(url, payload, config);
				break;
			case 'DELETE':
				response = await api.delete(url, config);
				break;
			default:
				throw new Error(`Unsupported method: ${method}`);
			}

			setData(response.data);
			return response.data;
		} catch (err) {
			// Enhanced error handling
			const errorMessage = sanitizeErrorMessage(err.response?.data?.message || err.message || 'An error occurred');
			setError(errorMessage);

			// Log security-relevant errors
			if (err.response?.status === 403) {
				console.warn('CSRF validation failed - possible CSRF attack');
			}

			throw err;
		} finally {
			setLoading(false);
		}
	}, [endpoint, method]);

	return { mutate, loading, error, data };
};

/**
 * Custom hook for cursor-paginated data (keyset pagination — "before"/"beforeId" style cursors,
 * not page numbers). The caller supplies a function that builds the endpoint URL from the
 * current cursor; this hook owns the cursor/back-stack state and delegates fetching to
 * useApiData, so refetches driven by a cursor change happen automatically via useApiData's
 * own endpoint-dependency effect — no separate manual refetch call is needed when the cursor
 * changes (only when it doesn't, e.g. a manual refresh on the first page).
 * @param {(cursor: any) => string} buildEndpoint - builds the endpoint URL for a given cursor (null = first page)
 * @param {Object} options - passed through to useApiData (transform, onSuccess, onError)
 * @returns {Object} { data, loading, error, refetch, cursor, isFirstPage, hasPrev, goNext, goPrev, goFirst }
 */
export const useCursorPaginatedData = (buildEndpoint, options = {}) => {
	const [cursor, setCursor] = useState(null); // null = first page
	const [cursorStack, setCursorStack] = useState([]);

	const endpoint = useMemo(() => buildEndpoint(cursor), [buildEndpoint, cursor]);
	const { data, loading, error, refetch } = useApiData(endpoint, [], options);

	const goNext = useCallback((nextCursor) => {
		setCursorStack(s => [...s, cursor]);
		setCursor(nextCursor);
	}, [cursor]);

	const goPrev = useCallback(() => {
		setCursorStack(s => {
			if (s.length === 0) return s;
			const copy = [...s];
			setCursor(copy.pop());
			return copy;
		});
	}, []);

	const goFirst = useCallback(() => {
		setCursorStack([]);
		setCursor(null);
	}, []);

	return {
		data,
		loading,
		error,
		refetch,
		cursor,
		isFirstPage: cursor === null,
		hasPrev: cursorStack.length > 0,
		goNext,
		goPrev,
		goFirst,
	};
};

/**
 * Custom hook for real-time data with polling
 * @param {string} endpoint - API endpoint
 * @param {number} interval - Polling interval in milliseconds
 * @param {Object} options - Configuration options
 * @returns {Object} { data, loading, error, startPolling, stopPolling }
 */
export const usePollingData = (endpoint, interval = 5000, options = {}) => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isPolling, setIsPolling] = useState(false);
	const intervalRef = useRef(null);
	const isMountedRef = useRef(true);
	const isPollingRef = useRef(false); // ref guard prevents state-change re-trigger loop

	const { immediate = true, transform = (data) => data } = options;
	const transformRef = useRef(transform);
	useEffect(() => { transformRef.current = transform; }); // keep ref current without adding to fetchData deps

	const fetchData = useCallback(async () => {
		try {
			setError(null);
			const response = await api.get(endpoint);

			if (isMountedRef.current) {
				const transformedData = transformRef.current(response.data);
				setData(transformedData);
			}
		} catch (err) {
			if (isMountedRef.current) {
				const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
				setError(errorMessage);
			}
		} finally {
			if (isMountedRef.current) {
				setLoading(false);
			}
		}
	}, [endpoint]); // transform removed — accessed via ref to avoid recreating on every render

	const startPolling = useCallback(() => {
		if (!isPollingRef.current) {
			isPollingRef.current = true;
			setIsPolling(true);
			intervalRef.current = setInterval(fetchData, interval);
		}
	}, [fetchData, interval]); // isPolling state removed — ref used for guard

	const stopPolling = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
			isPollingRef.current = false;
			setIsPolling(false);
		}
	}, []);

	useEffect(() => {
		if (immediate) {
			fetchData();
			if (interval > 0) {
				startPolling();
			}
		}

		return () => {
			stopPolling();
			isMountedRef.current = false;
		};
	}, [fetchData, immediate, interval, startPolling, stopPolling]);

	return { data, loading, error, startPolling, stopPolling, isPolling };
};
